import { useState, useCallback } from "react";
import {
  updateProfile,
  verifyBeforeUpdateEmail,
  reauthenticateWithPopup,
  unlink,
  GoogleAuthProvider,
} from "firebase/auth";
import { auth } from "../config/firebase";

export interface UserProfileData {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  createdAt: string | null;
  lastSignInTime: string | null;
  emailVerified: boolean;
  providers: string[];
}

export interface ProfileUpdatePayload {
  displayName?: string;
  photoURL?: string;
}

export interface ProfileError {
  code: string;
  message: string;
}

export function useUserProfile() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ProfileError | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfileData | null>(null);

  const loadUserProfile = useCallback(async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      setUserProfile(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Reload user to get latest data from Firebase
      await currentUser.reload();

      // Extract provider information
      const providers = currentUser.providerData.map((p) => p.providerId);

      setUserProfile({
        uid: currentUser.uid,
        email: currentUser.email,
        displayName: currentUser.displayName,
        photoURL: currentUser.photoURL,
        createdAt: currentUser.metadata?.creationTime || null,
        lastSignInTime: currentUser.metadata?.lastSignInTime || null,
        emailVerified: currentUser.emailVerified,
        providers,
      });
    } catch (err: any) {
      const errorObj = {
        code: err.code || "unknown_error",
        message: err.message || "Failed to load profile",
      };
      setError(errorObj);
    } finally {
      setLoading(false);
    }
  }, []);

  const updateUserProfile = useCallback(
    async (updates: ProfileUpdatePayload) => {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error("No user logged in");
      }

      try {
        setLoading(true);
        setError(null);

        await updateProfile(currentUser, updates);
        await currentUser.reload();

        // Update local state
        await loadUserProfile();

        return { success: true };
      } catch (err: any) {
        const errorObj = {
          code: err.code || "unknown_error",
          message: err.message || "Failed to update profile",
        };
        setError(errorObj);
        throw errorObj;
      } finally {
        setLoading(false);
      }
    },
    [loadUserProfile]
  );

  const updateEmail = useCallback(
    async (newEmail: string) => {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error("No user logged in");
      }

      try {
        setLoading(true);
        setError(null);

        // Firebase requires verification email
        await verifyBeforeUpdateEmail(currentUser, newEmail);

        return {
          success: true,
          message: "Verification email sent. Please check your email to confirm the change.",
        };
      } catch (err: any) {
        const errorObj = {
          code: err.code || "unknown_error",
          message: err.message || "Failed to update email",
        };
        setError(errorObj);
        throw errorObj;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const unlinkProvider = useCallback(
    async (providerId: string) => {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error("No user logged in");
      }

      try {
        setLoading(true);
        setError(null);

        // Reauthenticate before unlinking
        const provider = new GoogleAuthProvider();
        await reauthenticateWithPopup(currentUser, provider);

        // Unlink the provider
        await unlink(currentUser, providerId);

        // Reload and update profile
        await currentUser.reload();
        await loadUserProfile();

        return { success: true };
      } catch (err: any) {
        const errorObj = {
          code: err.code || "unknown_error",
          message: err.message || "Failed to unlink provider",
        };
        setError(errorObj);
        throw errorObj;
      } finally {
        setLoading(false);
      }
    },
    [loadUserProfile]
  );

  const downloadAccountData = useCallback(async () => {
    const currentUser = auth.currentUser;
    if (!currentUser || !userProfile) {
      throw new Error("No user logged in");
    }

    try {
      const accountData = {
        uid: userProfile.uid,
        email: userProfile.email,
        displayName: userProfile.displayName,
        photoURL: userProfile.photoURL,
        createdAt: userProfile.createdAt,
        lastSignInTime: userProfile.lastSignInTime,
        emailVerified: userProfile.emailVerified,
        providers: userProfile.providers,
        exportedAt: new Date().toISOString(),
      };

      const dataStr = JSON.stringify(accountData, null, 2);
      const dataBlob = new Blob([dataStr], { type: "application/json" });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `gavekort-account-data-${new Date().toISOString().split("T")[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      return { success: true };
    } catch (err: any) {
      const errorObj = {
        code: err.code || "unknown_error",
        message: err.message || "Failed to download account data",
      };
      setError(errorObj);
      throw errorObj;
    }
  }, [userProfile]);

  return {
    userProfile,
    loading,
    error,
    loadUserProfile,
    updateUserProfile,
    updateEmail,
    unlinkProvider,
    downloadAccountData,
  };
}
